import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { fmtCount } from '../StreamIngest/vocabulary'

// Step 6b's friend pushes per UTC day and kind: held and sending, the rows as
// they stand now (the delivery read's `friends.days`), then each push sent or
// dropped, one per send (`friend_pushes`), the dropped by what the send-time
// recheck found. Newest first, as the server sends them. A state or outcome
// this page does not name yet gets a column of its own under its key rather
// than vanishing.

export const KIND_WORDS = {
  friend_quotes: 'friend quotes',
  friend_request: 'friend request',
  friend_accepted: 'friend accepted',
}

const STATE_COLUMNS = [
  {
    key: 'held',
    label: 'Held',
    help: 'Held a short while before sending, in case there’s more to say.',
  },
  {
    key: 'sending',
    label: 'Sending',
    help: 'Mid-send. Stuck for over two minutes counts as past due.',
  },
  {
    key: 'sent',
    label: 'Sent',
    help: 'The push went out.',
  },
]

const DROPPED_COLUMNS = [
  {
    key: 'dropped:not_readable',
    label: 'not readable',
    help: 'The friendship ended, or someone blocked.',
  },
  {
    key: 'dropped:no_news',
    label: 'no news',
    help: 'Nothing new left to say: the quote was taken back, withheld, or the request already answered.',
  },
  {
    key: 'dropped:ceiling',
    label: 'ceiling',
    help: 'Already told about enough friends today.',
  },
  {
    key: 'dropped:not_in_port',
    label: 'not in port',
    help: 'The reader no longer runs the merged app (no synced stream_port key).',
  },
  {
    key: 'dropped:send_failed',
    label: 'send failed',
    help: 'Braze refused the push or did not answer.',
    tone: 'error.main',
  },
  {
    key: 'dropped:lost',
    label: 'lost',
    help: 'Its task or its send never ran; the hourly sweep wrote it off.',
    tone: 'error.main',
  },
]

const keyOf = (row) =>
  row.state === 'dropped' ? `dropped:${row.outcome ?? 'none'}` : row.state

const FriendPushTable = ({ days }) => {
  if (!days.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No friend push changed in this window.
      </Typography>
    )
  }

  const known = new Set(
    [...STATE_COLUMNS, ...DROPPED_COLUMNS].map(({ key }) => key)
  )
  const extraStates = []
  const extraDropped = []
  const grouped = new Map()
  days.forEach((row) => {
    const id = `${row.day}|${row.kind}`
    if (!grouped.has(id))
      grouped.set(id, { day: row.day, kind: row.kind, counts: {} })
    const key = keyOf(row)
    const counts = grouped.get(id).counts
    counts[key] = (counts[key] || 0) + row.count
    if (!known.has(key)) {
      known.add(key)
      const extra = { key, help: 'Not named on this page yet.' }
      if (key.startsWith('dropped:'))
        extraDropped.push({ ...extra, label: key.slice('dropped:'.length) })
      else extraStates.push({ ...extra, label: key })
    }
  })
  const states = [...STATE_COLUMNS, ...extraStates]
  const dropped = [...DROPPED_COLUMNS, ...extraDropped]

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2}>Day (UTC)</TableCell>
            <TableCell rowSpan={2}>Kind</TableCell>
            <TableCell colSpan={states.length} align="center">
              Held now · sent
            </TableCell>
            <TableCell colSpan={dropped.length} align="center">
              Dropped, by outcome
            </TableCell>
          </TableRow>
          <TableRow>
            {[...states, ...dropped].map(({ key, label, help }) => (
              <TableCell key={key} align="right">
                <Tooltip title={help}>
                  <span>{label}</span>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[...grouped.values()].map(({ day, kind, counts }) => (
            <TableRow key={`${day}|${kind}`}>
              <TableCell>{day}</TableCell>
              <TableCell>{KIND_WORDS[kind] || kind}</TableCell>
              {[...states, ...dropped].map(({ key, tone }) => {
                const n = counts[key] || 0
                return (
                  <TableCell
                    key={key}
                    align="right"
                    sx={{
                      color: n ? tone : 'text.disabled',
                      fontWeight: n && tone ? 700 : undefined,
                    }}
                  >
                    {fmtCount(n)}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default FriendPushTable
